import React, { useRef, useState } from "react";
import { formatToPeso } from "../../../util/helperFunctions";

// The backend accepts at most 5 slots per file kind per line item (see multer config).
export const EPR_MAX_FILES_PER_KIND = 5;

const FILE_KINDS = [
  { key: "attachments", label: "Attachment" },
  { key: "files", label: "Supporting file" },
];

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const isImage = (type = "", name = "") =>
  type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);

function FileChip({ entry, onRemove, disabled }) {
  const preview = entry.kind === "new" ? entry.previewUrl : entry.url;
  const showThumb = isImage(entry.type, entry.name) && preview;

  return (
    <li className="prq-file">
      <span className="prq-file__thumb" aria-hidden="true">
        {showThumb ? (
          <img src={preview} alt="" />
        ) : (
          <i className="fa fa-file-o"></i>
        )}
      </span>
      <span className="prq-file__meta">
        {entry.kind === "existing" ? (
          <a href={entry.url} target="_blank" rel="noreferrer">
            {entry.name}
          </a>
        ) : (
          <span className="prq-file__name">{entry.name}</span>
        )}
        <small>
          {entry.kind === "existing" ? "Saved" : formatSize(entry.size)}
        </small>
      </span>
      <button
        type="button"
        className="prq-file__remove"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${entry.name}`}
      >
        <i className="fa fa-times" aria-hidden="true"></i>
      </button>
    </li>
  );
}

function EprLineItem({
  item,
  index,
  errors = {},
  disabled = false,
  canRemove = true,
  filesAllowed = true,
  onChange,
  onRemove,
  onDuplicate,
}) {
  const inputRefs = {
    attachments: useRef(null),
    files: useRef(null),
  };
  const [noteOpen, setNoteOpen] = useState(Boolean(item.remarks));
  const [fileNotice, setFileNotice] = useState("");

  const amount = (Number(item.quantity) || 0) * (Number(item.price) || 0);
  const descError = errors[`items.${item.key}.description`];
  const qtyError = errors[`items.${item.key}.quantity`];

  const update = (patch) => onChange(item.key, patch);

  const handleNumber = (field) => (e) => {
    const raw = e.target.value;
    // Keep what the user typed (including "" and "1.") so the input doesn't fight them.
    if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) update({ [field]: raw });
  };

  const handleFilesPicked = (kind) => (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;

    const room = EPR_MAX_FILES_PER_KIND - item[kind].length;
    const accepted = picked.slice(0, Math.max(room, 0)).map((file) => ({
      kind: "new",
      uid: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      previewUrl: URL.createObjectURL(file),
    }));

    setFileNotice(
      accepted.length < picked.length
        ? `Only ${EPR_MAX_FILES_PER_KIND} files per type can be attached to an item.`
        : "",
    );
    if (accepted.length > 0) update({ [kind]: [...item[kind], ...accepted] });
  };

  const removeFile = (kind, entry) => {
    if (entry.kind === "new") URL.revokeObjectURL(entry.previewUrl);
    setFileNotice("");
    update({
      [kind]: item[kind].filter((f) =>
        entry.kind === "new" ? f.uid !== entry.uid : f.id !== entry.id,
      ),
    });
  };

  const fileCount = item.attachments.length + item.files.length;

  return (
    <div className="prq-item" id={`prq-item-${item.key}`}>
      <div className="prq-item__main">
        <span className="prq-item__no">{index + 1}</span>

        <div className="prq-field prq-item__desc">
          <label className="prq-item__label" htmlFor={`prq-desc-${item.key}`}>
            Description
          </label>
          <textarea
            id={`prq-desc-${item.key}`}
            rows={2}
            value={item.description}
            disabled={disabled}
            placeholder="What do you need? Include brand, size or spec."
            aria-invalid={Boolean(descError)}
            onChange={(e) => update({ description: e.target.value })}
          />
          {descError && <p className="prq-error">{descError}</p>}
        </div>

        <div className="prq-item__numbers">
          <div className="prq-field">
            <label className="prq-item__label" htmlFor={`prq-qty-${item.key}`}>
              Qty
            </label>
            <input
              id={`prq-qty-${item.key}`}
              type="text"
              inputMode="decimal"
              value={item.quantity}
              disabled={disabled}
              placeholder="0"
              aria-invalid={Boolean(qtyError)}
              onChange={handleNumber("quantity")}
            />
            {qtyError && <p className="prq-error">{qtyError}</p>}
          </div>

          <div className="prq-field">
            <label
              className="prq-item__label"
              htmlFor={`prq-price-${item.key}`}
            >
              Unit price
            </label>
            <input
              id={`prq-price-${item.key}`}
              type="text"
              inputMode="decimal"
              value={item.price}
              disabled={disabled}
              placeholder="0.00"
              onChange={handleNumber("price")}
            />
          </div>

          <div className="prq-field prq-item__amount">
            <span className="prq-item__label">Amount</span>
            <output>{formatToPeso(amount)}</output>
          </div>
        </div>

        <div className="prq-item__actions">
          <button
            type="button"
            className="prq-iconbtn"
            onClick={() => onDuplicate(item.key)}
            disabled={disabled}
            title="Duplicate item"
            aria-label={`Duplicate item ${index + 1}`}
          >
            <i className="fa fa-clone" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            className="prq-iconbtn prq-iconbtn--danger"
            onClick={() => onRemove(item.key)}
            disabled={disabled || !canRemove}
            title="Remove item"
            aria-label={`Remove item ${index + 1}`}
          >
            <i className="fa fa-trash-o" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div className="prq-item__extras">
        <div className="prq-item__links">
          <button
            type="button"
            className="prq-linkbtn"
            onClick={() => setNoteOpen((open) => !open)}
            aria-expanded={noteOpen}
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
            {noteOpen ? "Hide note" : item.remarks ? "Edit note" : "Add note"}
          </button>

          {FILE_KINDS.map(({ key, label }) => (
            <React.Fragment key={key}>
              <input
                ref={inputRefs[key]}
                type="file"
                multiple
                hidden
                onChange={handleFilesPicked(key)}
              />
              <button
                type="button"
                className="prq-linkbtn"
                disabled={
                  disabled ||
                  !filesAllowed ||
                  item[key].length >= EPR_MAX_FILES_PER_KIND
                }
                onClick={() => inputRefs[key].current?.click()}
              >
                <i className="fa fa-paperclip" aria-hidden="true"></i>
                {label}
                {item[key].length > 0 && (
                  <span className="prq-count">
                    {item[key].length}/{EPR_MAX_FILES_PER_KIND}
                  </span>
                )}
              </button>
            </React.Fragment>
          ))}
        </div>

        {noteOpen && (
          <div className="prq-field prq-item__note">
            <label className="prq-sr" htmlFor={`prq-note-${item.key}`}>
              Note for item {index + 1}
            </label>
            <textarea
              id={`prq-note-${item.key}`}
              rows={2}
              value={item.remarks}
              disabled={disabled}
              placeholder="Delivery instructions, alternatives, reason for request…"
              onChange={(e) => update({ remarks: e.target.value })}
            />
          </div>
        )}

        {fileNotice && <p className="prq-error">{fileNotice}</p>}

        {fileCount > 0 && (
          <div className="prq-item__files">
            {FILE_KINDS.map(({ key, label }) =>
              item[key].length > 0 ? (
                <div className="prq-filegroup" key={key}>
                  <span className="prq-filegroup__title">{label}s</span>
                  <ul>
                    {item[key].map((entry) => (
                      <FileChip
                        key={entry.kind === "new" ? entry.uid : `e-${entry.id}`}
                        entry={entry}
                        disabled={disabled}
                        onRemove={() => removeFile(key, entry)}
                      />
                    ))}
                  </ul>
                </div>
              ) : null,
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EprLineItem;
